import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { CalendarDays, Loader2, RefreshCw, Link2Off } from 'lucide-react';
import { useGoogleCalendar } from '@/hooks/useGoogleCalendar';

export function GoogleCalendarCard() {
  const { status, loading, syncing, connecting, connect, disconnect, sync, updatePrefs } = useGoogleCalendar();

  const prefs = status?.prefs;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5" />
          Google Calendar
        </CardTitle>
        <CardDescription>
          Publica tus órdenes de trabajo y ausencias en tu Google Calendar (solo lectura en Google).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Comprobando conexión…
          </div>
        ) : !status?.connected ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Aún no has conectado tu cuenta de Google. Al conectar, Raven creará un calendario llamado
              «Raven» y mantendrá allí tus intervenciones actualizadas.
            </p>
            <Button onClick={connect} disabled={connecting}>
              {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CalendarDays className="mr-2 h-4 w-4" />}
              Conectar Google Calendar
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">Conectado</Badge>
              {status.email && <span className="text-sm text-muted-foreground">{status.email}</span>}
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="sync-ots">Órdenes de trabajo</Label>
                  <p className="text-xs text-muted-foreground">Las OT asignadas a ti con fecha planificada.</p>
                </div>
                <Switch
                  id="sync-ots"
                  checked={prefs?.sync_ots ?? true}
                  onCheckedChange={(v) => updatePrefs({ sync_ots: v })}
                />
              </div>

              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label htmlFor="sync-ausencias">Ausencias</Label>
                  <p className="text-xs text-muted-foreground">Vacaciones y ausencias aprobadas, como eventos de día completo.</p>
                </div>
                <Switch
                  id="sync-ausencias"
                  checked={prefs?.sync_ausencias ?? true}
                  onCheckedChange={(v) => updatePrefs({ sync_ausencias: v })}
                />
              </div>

              {!!status.calendarios?.length && (
                <div className="space-y-2">
                  <Label>Calendario de destino</Label>
                  <Select
                    value={prefs?.calendar_id ?? undefined}
                    onValueChange={(v) => updatePrefs({ calendar_id: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un calendario" />
                    </SelectTrigger>
                    <SelectContent>
                      {status.calendarios.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.summary}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Separator />

            <div className="flex flex-wrap items-center gap-3">
              <Button variant="outline" onClick={() => sync()} disabled={syncing}>
                {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Sincronizar ahora
              </Button>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" className="text-destructive hover:text-destructive">
                    <Link2Off className="mr-2 h-4 w-4" />
                    Desconectar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Desconectar Google Calendar?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Se revocará el acceso y se eliminarán de tu Google Calendar los eventos creados por Raven.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => disconnect(true)}>Desconectar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              {prefs?.last_sync_at && (
                <span className="text-xs text-muted-foreground">
                  Última sincronización: {new Date(prefs.last_sync_at).toLocaleString('es-ES')}
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
